/**
 * ⭐⭐ board 2814 M5 (plan DD-9) - "ADD A PHONED-IN TRIP", the one part this file holds.
 *
 * ⭐ board 2812 M5 (step 2): T1746 M4's account-booking TABLE that lived here (every
 * account-billed run, across all homes, read from `GET /api/nursing/bookings`) is gone
 * with its route and its client read: since board 2812 M4 the Nursing homes Bookings tab
 * is the SHARED list (`BookingsList`, `GET /api/bookings?who=homes`), and nothing reached
 * the table. The form stays, hosted by that tab (`NursingHomes.tsx`), scoped to a home
 * on a home's page.
 *
 * ⛔ THE FEEDBACK WIDGET'S SCREENSHOT IS A RECORDED RESIDUAL here too: `data-fb-opaque`
 * on a control covers the CLICK trail only, never the widget's JPEG, which reproduces
 * rendered text verbatim. The widget is build-gated OFF (scripts/build-client.mjs needs
 * ECMB_FEEDBACK_KIOSK=1 AND ECMB_TEST_DEPLOY=1). Full statement:
 * screens/admin/AccountList.tsx.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { Button, Card, FormField } from '@ecmb/ui';
import {
  listAccounts,
  listDestinations,
  resolveCapturedPrice,
  bookPhoneIn,
  type ApiAccount,
  type ApiDestination,
  type CapturedPriceResolution,
  type PhoneInBooked,
} from '../../lib/actions.js';
import { civilDateLabel, euro } from '../../lib/format.js';
import { parseEuroToCents } from '../../lib/price-actions.js';
import { PAYER_WORDS } from '../../lib/payer-words.js';
import { BANDS } from '../../../engine/bands.js';
import { dublinCivilDate } from '../../../shared/dublin-date.js';
import { Banner } from '../../components/Banner.js';

/** ⭐ board 2814 M5 - what the phone-in press did, in words, with its booking. */
type Outcome = { text: string; bookingId: number };

/**
 * "Booked at €90. It is booking KLNH 07 for Tue, 13 Oct." (or the `already` words).
 * ⭐ wave-1 security S3: the figure is the one the SERVER recorded, so a price that
 * moved between the form's read and the press is said here.
 */
function outcomeText(res: PhoneInBooked): string {
  const ref = res.booking.jobRef ?? `#${String(res.booking.bookingId)}`;
  const day = res.booking.journeyDate === null ? '' : civilDateLabel(res.booking.journeyDate, null);
  const when = day === '' ? '' : ` for ${day}`;
  const at = res.booking.totalCents === null ? '' : ` at ${euro(res.booking.totalCents)}`;
  return res.outcome === 'already'
    ? `That trip was already booked${at}. It is booking ${ref}${when}. Nothing new was made.`
    : `Booked${at}. It is booking ${ref}${when}.`;
}

/**
 * ⭐⭐ board 2814 M5 (plan DD-9) - "ADD A PHONED-IN TRIP": the toggle, the form, and what
 * its press did. ⭐ board 2812 M4 (plan step 4): hosted by the Bookings tab of Nursing
 * homes, where a home's page SCOPES it to that home (`fixedAccountId`: the home is chosen
 * and named, never picked). `onBooked` asks the host for a fresh read of its list.
 */
export function PhoneInTrip({
  onOpenBooking,
  onBooked,
  fixedAccountId,
}: {
  onOpenBooking: (bookingId: number) => void;
  onBooked: () => void;
  fixedAccountId?: number;
}) {
  /** ⭐ board 2814 M5 (plan DD-9): the phoned-in trip form, and what its press did. */
  const [adding, setAdding] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const outcomeRef = useRef<HTMLDivElement>(null);

  // ⛔ THE KEYBOARD FOLLOWS THE OUTCOME. The form it was in has just left the page,
  // so without this the caret falls back to <body> and the words are never reached.
  useEffect(() => {
    if (outcome !== null) outcomeRef.current?.focus();
  }, [outcome]);

  return (
    <>
      {/* ⭐ board 2814 M5 (plan DD-9): a trip a home phoned in, booked here without
          the calculator. ONE-OFF TRIPS ONLY (DD-8): the form has no repeat option.
          ⭐ wave-1 ux U3: the control is the form's one toggle (`aria-expanded`), so
          the form needs no Close of its own. It is secondary, so the form's press is
          the screen's one gold. */}
      <div class="action-row">
        <Button
          variant="secondary"
          ariaExpanded={adding}
          onClick={() => {
            setOutcome(null);
            setAdding(!adding);
          }}
        >
          Add a phoned-in trip
        </Button>
      </div>

      {adding && (
        <>
          <PhoneInForm
            onOpenBooking={onOpenBooking}
            fixedAccountId={fixedAccountId}
            onBooked={(res) => {
              setAdding(false);
              setOutcome({ text: outcomeText(res), bookingId: res.booking.bookingId });
              onBooked();
            }}
          />
          {/* ⭐ board 2812 fix round (Captain's /check item 4): the open form ends at a
              section rule, the house separator, so its gold never sits directly on the
              list's Find row below it. */}
          <hr class="section-rule" />
        </>
      )}

      {/* ⛔ wave-1 ux U5: the outcome TAKES THE CARET (the house pattern for the
          result of a press), so it is not also a live region: one announcement,
          not two. */}
      {outcome !== null && (
        <div class="phone-in-block" tabIndex={-1} ref={outcomeRef}>
          <Card shape="row">
            <p class="phone-in-line">
              {outcome.text}{' '}
              <Button variant="quiet" size="inline" onClick={() => onOpenBooking(outcome.bookingId)}>
                Open the booking
              </Button>
            </p>
          </Card>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ⭐⭐ board 2814 M5 (plan DD-9) - "ADD A PHONED-IN TRIP"
// ---------------------------------------------------------------------------

/** The 'Other / not listed' choice. A listed destination's value is its id, which is
 *  digits only, so no catalogue entry can collide with this word. */
const OTHER = 'other';

/** DD-5's words for a cash trip, the ones booking detail and the Requests step use. */
const CASH_WORDS = 'Cash, paid to the driver on the day';

/** `null` = that read FAILED, distinct from `[]` (it worked and found nothing):
 *  "could not load" and "none set up" are different facts to act on. */
type PhoneInLists = {
  homes: ApiAccount[] | null;
  destinations: ApiDestination[] | null;
  homesTruncated: boolean;
  destinationsTruncated: boolean;
};

type AgreedState = { kind: 'idle' } | { kind: 'checking' } | { kind: 'failed' } | CapturedPriceResolution;

/** The double-tap key, made once when the form opens. `getRandomValues` rather than
 *  `randomUUID`, which a browser offers only in a secure context. */
function newPhoneInKey(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => (b + 256).toString(16).slice(1)).join('');
}

type PhoneInFormProps = {
  onBooked: (res: PhoneInBooked) => void;
  /** A key that booked a different trip offers that booking (wave-1 security S1). */
  onOpenBooking: (bookingId: number) => void;
  /** ⭐ board 2812 M4: on a home's page, that home's account: chosen, named, not picked. */
  fixedAccountId?: number;
};

/** What a failed press says, and where the caret goes: the FIRST invalid field
 *  (wave-1 ux U1) or, for the server's answer, the banner itself. */
type Failure = { text: string; fieldId: string | null; openBookingId?: number };

/**
 * The form, then ONE confirm step in place of its press (DESIGN.md §5.1): the
 * payer in words and the price, "Yes, book it" and "Not now".
 *
 * ⛔ EVERYTHING HERE IS ADVISORY TO THE SERVER. The home, the destination and the
 * agreed price are re-read and ruled on by `POST /api/nursing/phone-ins`; this form
 * shows the agreed price so Helen can say it on the phone, and never sends it.
 * ⛔ NO CUSTOMER IS NAMED OR CREATED: there is no name or phone field, and the
 * traveller reference is v33's short, non-clinical handle (a room number).
 */
function PhoneInForm({ onBooked, onOpenBooking, fixedAccountId }: PhoneInFormProps) {
  // ⛔ ONE KEY PER OPENING OF THE FORM, kept across every press and every failure,
  // so a re-press after an answer that never arrived cannot book twice.
  const [phoneInKey] = useState(newPhoneInKey);
  const [lists, setLists] = useState<PhoneInLists | null>(null);
  const [listsSeq, setListsSeq] = useState(0);
  const [homeId, setHomeId] = useState(fixedAccountId === undefined ? '' : String(fixedAccountId));
  const [destinationPick, setDestinationPick] = useState('');
  const [typedDestination, setTypedDestination] = useState('');
  const [journeyDate, setJourneyDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [tripKind, setTripKind] = useState('');
  const [returnTime, setReturnTime] = useState('');
  const [band, setBand] = useState('');
  const [wheelchair, setWheelchair] = useState('');
  const [payer, setPayer] = useState('');
  const [amount, setAmount] = useState('');
  const [travellerRef, setTravellerRef] = useState('');
  /** wave-1 ux U3: the reference sits behind a quiet disclosure; a typed value is
   *  kept (and repeated on the confirm step) whether or not the field is shown. */
  const [travellerOpen, setTravellerOpen] = useState(false);
  /** ⭐ board 2812 fix round (item 4): the toggle sits below the field it opens, so its
   *  press hands the caret to the field (once, on that press). */
  const travellerFocus = useRef(false);
  useEffect(() => {
    if (!travellerOpen || !travellerFocus.current) return;
    travellerFocus.current = false;
    document.getElementById('phone-in-traveller')?.focus();
  }, [travellerOpen]);
  const [agreed, setAgreed] = useState<AgreedState>({ kind: 'idle' });
  const [stage, setStage] = useState<'form' | 'confirm'>('form');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  /** Every failed press re-announces and re-focuses, even with the same words. */
  const [failureSeq, setFailureSeq] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const failureRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const pressRef = useRef<HTMLButtonElement>(null);
  const backFromConfirm = useRef(false);
  const agreedSeq = useRef(0);
  const inFlight = useRef(false);
  /** wave-1 ux U4: the day picker starts at today in Dublin (the server still refuses
   *  a past day; this only keeps one from being picked). */
  const [today] = useState(() => dublinCivilDate(new Date()));

  const fail = (next: Failure): void => {
    setFailure(next);
    setFailureSeq((n) => n + 1);
  };
  /** A field's message, while the failure still names it. */
  const errorOf = (id: string): string | undefined =>
    failure !== null && failure.fieldId === id ? failure.text : undefined;
  /** An edit to the field the failure names clears its mark. */
  const edit = (id: string, set: (v: string) => void) => (v: string): void => {
    set(v);
    if (failure !== null && failure.fieldId === id) setFailure(null);
  };

  useEffect(() => { headingRef.current?.focus(); }, []);

  // ⛔ EACH LIST HAS ITS OWN `.catch()`: one dead route must not take the other
  // picker down (the nursing tile's rule). A body that is not the wire shape is a
  // FAILED read, never an empty book.
  useEffect(() => {
    let live = true;
    void Promise.all([listAccounts().catch(() => null), listDestinations().catch(() => null)])
      .then(([accounts, destinations]) => {
        if (!live) return;
        setLists({
          homes: accounts !== null && Array.isArray(accounts.rows)
            ? accounts.rows.filter((a) => a.active && a.kind === 'nursing-home')
            : null,
          destinations: destinations !== null && Array.isArray(destinations.rows)
            ? destinations.rows.filter((d) => d.active)
            : null,
          homesTruncated: accounts?.truncated === true,
          destinationsTruncated: destinations?.truncated === true,
        });
      })
      .catch(() => {
        if (live) setLists({ homes: null, destinations: null, homesTruncated: false, destinationsTruncated: false });
      });
    return () => { live = false; };
  }, [listsSeq]);

  const homes = lists?.homes ?? [];
  const destinations = lists?.destinations ?? [];
  const home = homes.find((h) => String(h.accountId) === homeId) ?? null;
  const typed = destinationPick === OTHER;
  const listed = typed || destinationPick === ''
    ? null
    : destinations.find((d) => String(d.destinationId) === destinationPick) ?? null;
  const isReturn = tripKind === 'return';
  /** wave-1 ux U7: the price lines say nothing until a home AND a destination are chosen. */
  const bothChosen = home !== null && (typed || listed !== null);

  // The agreed price, read BY KEY through the one resolver (D-RESOLVE), for display.
  // A stale answer for an earlier pick is dropped.
  useEffect(() => {
    agreedSeq.current += 1;
    const seq = agreedSeq.current;
    if (home === null || listed === null) {
      setAgreed({ kind: 'idle' });
      return;
    }
    setAgreed({ kind: 'checking' });
    void resolveCapturedPrice(home.accountId, listed.name).then(
      (r) => { if (seq === agreedSeq.current) setAgreed(r); },
      () => { if (seq === agreedSeq.current) setAgreed({ kind: 'failed' }); },
    );
  }, [home?.accountId ?? null, listed?.destinationId ?? null]);

  // ⛔ wave-1 ux U1: a check that fails moves the caret to the FIRST invalid field,
  // which carries `aria-invalid` and its message through `aria-describedby`; the
  // banner (an alert, re-mounted per press) is the announcement. The server's own
  // answer has no field, so the caret goes to the banner.
  useEffect(() => {
    if (failure === null) return;
    if (failure.fieldId !== null) document.getElementById(failure.fieldId)?.focus();
    else failureRef.current?.focus();
  }, [failureSeq]);

  // ⛔ THE KEYBOARD FOLLOWS THE STEP: the press it was on leaves the page when the
  // step replaces it, and comes back when "Not now" closes it.
  useEffect(() => {
    if (stage === 'confirm') confirmRef.current?.focus();
    else if (backFromConfirm.current) {
      backFromConfirm.current = false;
      pressRef.current?.focus();
    }
  }, [stage]);

  const oneWayCents = agreed.kind === 'priced' ? agreed.priceCents : null;
  // ⛔ A RETURN IS THE ONE-WAY FIGURE TWICE (the generator's rule), said in words.
  const agreedTripCents = oneWayCents === null ? null : isReturn ? oneWayCents * 2 : oneWayCents;
  const amountCents = amount.trim() === '' ? null : parseEuroToCents(amount);
  const destinationName = typed ? typedDestination.trim() : listed?.name ?? '';

  function agreedLine(): string {
    if (!bothChosen) return '';
    if (typed) return 'A typed address has no agreed price. Type the price below.';
    switch (agreed.kind) {
      case 'idle':
      case 'checking':
        return 'Checking the agreed price…';
      case 'failed':
        return 'Could not check the agreed price. Type the price below.';
      case 'missing':
      case 'unpriced':
        return 'There is no agreed price for this home and destination. Type the price below.';
      case 'priced':
        return isReturn
          ? `The agreed price is ${euro(agreed.priceCents * 2)} for the return, twice the ${euro(agreed.priceCents)} one way price.`
          : `The agreed price is ${euro(agreed.priceCents)} one way.`;
    }
  }

  /** The first thing missing or wrong, with the field it belongs to, or null. */
  function problem(): Failure | null {
    const at = (fieldId: string, text: string): Failure => ({ fieldId, text });
    if (home === null) return at('phone-in-home', 'Choose the nursing home.');
    if (destinationName === '') {
      return at(typed ? 'phone-in-address' : 'phone-in-destination', 'Choose a destination, or choose Other and type the address.');
    }
    if (tripKind === '') return at('phone-in-trip', 'Say whether the trip is one way or a return.');
    // ⭐ board 2812 fix round 2 (code-review L1): the checks run in the SCREEN's order, top
    // to bottom, so the first message and the caret land on the topmost missing field. The
    // size shares the trip kind's row since the fix round (item 4), above the day and times.
    if (band === '') return at('phone-in-band', 'Choose how many are travelling.');
    if (journeyDate === '') return at('phone-in-day', 'Choose the day of the trip.');
    // ⭐ wave-2 ux X2: the server's own rule (a day before today in Dublin), asked
    // before the step, read fresh so a form left open over midnight is still right.
    if (journeyDate < dublinCivilDate(new Date())) {
      return at('phone-in-day', 'This trip\u2019s date has passed. Choose today or a later day.');
    }
    if (pickupTime === '') return at('phone-in-pickup', 'Type the pickup time.');
    if (isReturn && (returnTime === '' || returnTime <= pickupTime)) {
      return at('phone-in-return', 'Type a return time after the pickup time.');
    }
    if (wheelchair === '') return at('phone-in-wheelchair', 'Say whether a wheelchair user is travelling.');
    if (payer === '') return at('phone-in-payer', 'Choose who pays.');
    if (amount.trim() !== '' && amountCents === null) {
      return at('phone-in-price', 'That price cannot be recorded. Type it in euro, like 120 or 120.50.');
    }
    if (amountCents === null && agreedTripCents === null) {
      return at('phone-in-price', agreed.kind === 'checking'
        ? 'Still checking the agreed price. Press again in a moment.'
        : 'There is no agreed price for this trip, so type the price.');
    }
    if (travellerRef.trim().length > 40) {
      setTravellerOpen(true);
      return at('phone-in-traveller', 'Keep the traveller reference to 40 characters or fewer.');
    }
    return null;
  }

  const check = (): void => {
    const wrong = problem();
    if (wrong !== null) {
      fail(wrong);
      return;
    }
    setFailure(null);
    setStage('confirm');
  };

  const notNow = (): void => {
    backFromConfirm.current = true;
    setStage('form');
  };

  const book = async (): Promise<void> => {
    if (inFlight.current || home === null || (payer !== 'hse' && payer !== 'house' && payer !== 'self')) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await bookPhoneIn({
        accountId: home.accountId,
        destination: typed ? destinationName : Number(destinationPick),
        journeyDate,
        pickupTime,
        returnTrip: isReturn,
        returnTime: isReturn ? returnTime : null,
        passengersBand: band,
        wheelchair: wheelchair === 'yes',
        payerKind: payer,
        amountCents,
        travellerRef: travellerRef.trim() === '' ? null : travellerRef.trim(),
        phoneInKey,
      });
      if (res.ok) {
        onBooked(res.value);
        return;
      }
      backFromConfirm.current = false;
      setStage('form');
      // ⭐ wave-2 ux X2: a date the server refused puts the caret on the day field,
      // with its message, as a failed check does.
      fail(res.code === 'phone_in_date_passed'
        ? { text: res.message, fieldId: 'phone-in-day' }
        : res.openBookingId === undefined
          ? { text: res.message, fieldId: null }
          : { text: res.message, fieldId: null, openBookingId: res.openBookingId });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const locked = stage === 'confirm';
  const payerOptions = [
    { value: '', label: 'Choose who pays' },
    { value: 'hse', label: PAYER_WORDS['hse'] ?? 'hse' },
    { value: 'house', label: PAYER_WORDS['house'] ?? 'house' },
    // ⛔ CASH ONLY FOR A TYPED ADDRESS: a listed destination is billed to the home
    // (the portal's `allowedPayerKinds` rule; the server refuses it too).
    ...(typed ? [{ value: 'self', label: CASH_WORDS }] : []),
  ];

  const payerLine = home === null
    ? ''
    : payer === 'self'
      ? `Who pays: ${CASH_WORDS}. ${home.name} is not billed for it.`
      : `Who pays: ${PAYER_WORDS[payer] ?? payer}. It is billed to ${home.name} at the end of the month.`;
  const priceLine = amountCents !== null
    ? `Price: ${euro(amountCents)}, the price you typed.`
    : agreedTripCents === null || oneWayCents === null
      ? ''
      : isReturn
        ? `Price: ${euro(agreedTripCents)}, the agreed price, twice the ${euro(oneWayCents)} one way price.`
        : `Price: ${euro(agreedTripCents)}, the agreed price.`;
  const tripLine = `${civilDateLabel(journeyDate, pickupTime)} to ${destinationName}${isReturn ? `, back at ${returnTime}` : ', one way'}.`;
  const travellerLine = travellerRef.trim() === '' ? '' : `Traveller reference: ${travellerRef.trim()}.`;
  /** ⭐ wave-2 ux X1: the dispatch facts, the band as Helen chose it. */
  const partyLine = `${band} travelling, ${wheelchair === 'yes' ? 'wheelchair accessible bus' : 'no wheelchair'}.`;

  return (
    // ⭐ wave-2 ux X4: the toggle above already reads "Add a phoned-in trip", so the
    // form's own heading (its focus target, and the region's name) does not repeat it.
    /* ⭐⭐ board 2812 fix round (Captain's /check item 4, R-2812-g): A FORM KEEPS THE
       MEASURE (`.phone-in-form`, 640px); "lists fill the width" is for lists. At 1920 its
       fields ran the pane's whole width. And its gold is reachable: two pairs of short
       answers share a row ("One way or return?" with the size, "Who pays?" with the price),
       and "Add a traveller reference" sits beside the gold rather than on a row of its
       own, so the form opened from the top of the tab ends inside a 1920x1080 screen.
       The wheelchair answer keeps its own full
       row: in a half row its longest answer is clipped (board 2814 M6, R9). */
    <section aria-label="New phoned-in trip" class="phone-in-form">
      <h3 class="screen-sub screen-sub--item" tabIndex={-1} ref={headingRef}>New phoned-in trip</h3>
      <p class="helper-note">A trip a home rang in. It is booked once, for one day.</p>

      {failure !== null && (
        <Banner key={failureSeq} kind="error" elementRef={failureRef} tabIndex={-1}>
          {failure.text}
          {failure.openBookingId !== undefined && (
            <>
              {' '}
              <Button variant="quiet" size="inline" onClick={() => onOpenBooking(failure.openBookingId as number)}>
                Open the booking
              </Button>
            </>
          )}
        </Banner>
      )}

      {/* ⛔ THE LOAD STATES DO NOT TAKE FOCUS: they render on open, not on a press. */}
      {lists !== null && lists.homes === null && (
        <Banner kind="error">
          Could not load the nursing homes.{' '}
          <Button variant="quiet" size="inline" onClick={() => setListsSeq((n) => n + 1)}>Try again</Button>
        </Banner>
      )}
      {lists !== null && lists.homes !== null && lists.homes.length === 0 && (
        <Banner kind="error">
          No nursing home accounts are set up yet. Mark the home as a nursing home in its account first.
        </Banner>
      )}
      {lists !== null && lists.destinations === null && (
        <Banner kind="error">
          Could not load the destination list. Choose Other and type the drop-off address instead.
        </Banner>
      )}
      {(lists?.homesTruncated === true || lists?.destinationsTruncated === true) && (
        <Banner kind="warn">
          A list is longer than this form can show. A destination that is missing can be typed under Other.
        </Banner>
      )}

      {/* ⭐ board 2812 M4: scoped to a home's page, the home is NAMED, not picked (one
          control fewer); the picker comes back only if that home is not a live nursing
          home on the loaded list, so the form can never book a home Helen cannot see. */}
      {fixedAccountId !== undefined && home !== null ? (
        <p class="helper-note" data-fb-opaque /* a home's name and its pickup address */>
          {`For ${home.name}. Pickup: ${[home.addressLine, home.town, home.eircode].filter((p) => p !== null && p !== '').join(', ') || 'no address on this account'}`}
        </p>
      ) : (
        <FormField
          id="phone-in-home"
          label="Nursing home"
          kind="select"
          opaque
          disabled={locked}
          value={homeId}
          onInput={edit('phone-in-home', setHomeId)}
          error={errorOf('phone-in-home')}
          options={[
            { value: '', label: lists === null ? 'Loading the homes…' : 'Choose the home' },
            ...homes.map((h) => ({ value: String(h.accountId), label: h.name })),
          ]}
          hint={home === null
            ? undefined
            : `Pickup: ${[home.addressLine, home.town, home.eircode].filter((p) => p !== null && p !== '').join(', ') || 'no address on this account'}`}
        />
      )}
      <FormField
        id="phone-in-destination"
        label="Destination"
        kind="select"
        opaque
        disabled={locked}
        value={destinationPick}
        onInput={(v) => {
          edit('phone-in-destination', setDestinationPick)(v);
          // Cash is offered for a typed address only, so a listed pick clears it.
          if (v !== OTHER && payer === 'self') setPayer('');
        }}
        error={errorOf('phone-in-destination')}
        options={[
          { value: '', label: lists === null ? 'Loading destinations…' : 'Choose a destination' },
          ...destinations.map((d) => ({ value: String(d.destinationId), label: d.name })),
          { value: OTHER, label: 'Other / not listed' },
        ]}
      />
      {typed && (
        <FormField
          id="phone-in-address"
          label="Drop-off address"
          opaque
          disabled={locked}
          value={typedDestination}
          onInput={edit('phone-in-address', setTypedDestination)}
          error={errorOf('phone-in-address')}
          maxLength={200}
          placeholder="Drop-off address or place"
        />
      )}
      {/* ⛔ THE AGREED PRICE, IN WORDS, BEFORE THE PRESS. A live region that exists
          before any answer lands, so a change is announced without taking focus. */}
      <p class="queue-evidence" role="status" aria-busy={agreed.kind === 'checking' ? 'true' : 'false'}>
        {agreedLine()}
      </p>
      {/* ⭐ board 2812 fix round (item 4): two short answers share a row; they wrap when
          narrow (the row's grid). ⛔ NO DEFAULT size: a guessed size is a silent
          mispricing (DD-2's rule). */}
      <div class="phone-in-row">
        <div class="phone-in-cell">
          <FormField
            id="phone-in-trip"
            label="One way or return?"
            kind="select"
            disabled={locked}
            value={tripKind}
            onInput={edit('phone-in-trip', setTripKind)}
            error={errorOf('phone-in-trip')}
            options={[
              { value: '', label: 'Choose one' },
              { value: 'one-way', label: 'One way' },
              { value: 'return', label: 'Return, the same day' },
            ]}
          />
        </div>
        <div class="phone-in-cell">
          <FormField
            id="phone-in-band"
            label="How many are travelling?"
            kind="select"
            disabled={locked}
            value={band}
            onInput={edit('phone-in-band', setBand)}
            error={errorOf('phone-in-band')}
            options={[{ value: '', label: 'Choose how many' }, ...BANDS.map((b) => ({ value: b, label: b }))]}
          />
        </div>
      </div>
      {/* ⭐ wave-1 ux U1: the day and the times share one line; they wrap when narrow. */}
      <div class="phone-in-row">
        <div class="phone-in-cell">
          <FormField
            id="phone-in-day"
            label="Day of the trip"
            type="date"
            min={today}
            disabled={locked}
            value={journeyDate}
            onInput={edit('phone-in-day', setJourneyDate)}
            error={errorOf('phone-in-day')}
          />
        </div>
        <div class="phone-in-cell">
          <FormField
            id="phone-in-pickup"
            label="Pickup time"
            type="time"
            disabled={locked}
            value={pickupTime}
            onInput={edit('phone-in-pickup', setPickupTime)}
            error={errorOf('phone-in-pickup')}
          />
        </div>
        {isReturn && (
          <div class="phone-in-cell">
            <FormField
              id="phone-in-return"
              label="Return time"
              type="time"
              disabled={locked}
              value={returnTime}
              onInput={edit('phone-in-return', setReturnTime)}
              error={errorOf('phone-in-return')}
            />
          </div>
        )}
      </div>
      {/* ⭐ board 2814 M6 (R9, M5's carried ux finding): the wheelchair answer stacks
          full width. In a half-row at 768 the select clipped "Yes, a wheelchair
          accessible bus" (and it would at the 640px measure too). */}
      {/* ⛔ NO DEFAULT: a stated wheelchair need reaches dispatch, so an answer
          nobody gave must not be recorded for them (the driver's ruling; Alex
          decides at M8). */}
      <FormField
        id="phone-in-wheelchair"
        label="Wheelchair user?"
        kind="select"
        disabled={locked}
        value={wheelchair}
        onInput={edit('phone-in-wheelchair', setWheelchair)}
        error={errorOf('phone-in-wheelchair')}
        options={[
          { value: '', label: 'Choose an answer' },
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes, a wheelchair accessible bus' },
        ]}
      />
      {/* ⭐ board 2812 fix round (item 4): who pays and the price share a row.
          ⛔ NO DEFAULT PAYER: the server refuses a trip without one. */}
      <div class="phone-in-row">
        <div class="phone-in-cell">
          <FormField
            id="phone-in-payer"
            label="Who pays?"
            kind="select"
            disabled={locked}
            value={payer}
            onInput={edit('phone-in-payer', setPayer)}
            error={errorOf('phone-in-payer')}
            options={payerOptions}
          />
        </div>
        <div class="phone-in-cell">
          <FormField
            id="phone-in-price"
            label="Price in euro"
            inputMode="decimal"
            disabled={locked}
            value={amount}
            onInput={edit('phone-in-price', setAmount)}
            error={errorOf('phone-in-price')}
            // ⭐ wave-2 ux X4: when the line above already says to type the price, the
            // field says nothing more; it speaks only when there is an agreed price to keep.
            hint={bothChosen && agreedTripCents !== null ? 'Leave it blank to use the agreed price.' : undefined}
          />
        </div>
      </div>
      {travellerOpen && (
        <FormField
          id="phone-in-traveller"
          label="Traveller reference (optional)"
          opaque
          disabled={locked}
          value={travellerRef}
          onInput={edit('phone-in-traveller', setTravellerRef)}
          error={errorOf('phone-in-traveller')}
          maxLength={40}
          hint="A room number or the home’s own short reference. No names, no conditions."
        />
      )}

      {stage === 'form'
        ? (
          /* ⭐ board 2812 fix round (item 4): "Add a traveller reference" sits beside the
             gold, after it, rather than on a row of its own above it. Its field opens
             above this row and takes the caret (`travellerFocus`), so the keyboard is
             never left below what it just opened. */
          <div class="action-row">
            <Button variant="primary" elementRef={pressRef} onClick={check}>Book this trip</Button>
            <Button
              variant="quiet"
              size="inline"
              ariaExpanded={travellerOpen}
              disabled={locked}
              onClick={() => {
                travellerFocus.current = !travellerOpen;
                setTravellerOpen(!travellerOpen);
              }}
            >
              Add a traveller reference
            </Button>
          </div>
        )
        : (
          /* ⭐ wave-1 ux U2: the step is its own block (the house card), set apart from
             the form above; each fact is its own line, the price last, above the pair. */
          <div
            class="phone-in-block"
            role="group"
            aria-label="Check the trip before booking it"
            tabIndex={-1}
            ref={confirmRef}
          >
            <Card>
              <p class="phone-in-line">{tripLine}</p>
              <p class="phone-in-line">{partyLine}</p>
              <p class="phone-in-line">{payerLine}</p>
              {travellerLine !== '' && <p class="phone-in-line">{travellerLine}</p>}
              <p class="phone-in-line">{priceLine}</p>
              <div class="action-row">
                <Button variant="primary" disabled={busy} onClick={() => void book()}>Yes, book it</Button>
                <Button variant="quiet" disabled={busy} onClick={notNow}>Not now</Button>
              </div>
            </Card>
          </div>
        )}
    </section>
  );
}
